import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
export interface Integration {
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  color: string;
  requiresRequest?: boolean;
}

export interface StaticIntegration {
  name: string;
  description: string;
  url: string;
  serverUrl?: string;
  requiresPremiumAccess: boolean;
  disabled?: boolean;
}

export interface IntegrationState {
  integrations: Integration[];
  commingSoonIntegrationsData: { name: string; description: string }[];
  staticIntegrationsData: StaticIntegration[];
  staticIntegrationsLoading: boolean;
  projectId: string | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export const useIntegrationStore = defineStore('integrationStore', {
  state: () =>
    ({
      integrations: [],
      commingSoonIntegrationsData: [
        {
          name: 'Google Workspace',
          description: 'Send step-by-step guides to Google Docs and Google Slides, and save videos directly to Google Drive',
        },
        {
          name: 'ServiceNow',
          description: 'Streamline IT service management with integrated screen recording and sharing',
        },
        {
          name: 'Freshdesk',
          description: 'Provide faster support resolution with visual troubleshooting guides and screen recordings',
        },
        {
          name: 'Linear',
          description: 'Attach videos, screenshots and more to your Linear tickets, create clear documentation from videos automatically',
        },
      ],
      staticIntegrationsData: [],
      staticIntegrationsLoading: false,
      projectId: null,
      token: null,
      loading: false,
      error: null,
    }) as IntegrationState,
  actions: {
    async fetchIntegrations(forceRefresh: boolean = false) {
      this.$patch({
        loading: true,
        error: null,
      });
      try {
        const response = await Api.integrations.list(forceRefresh);
        const integrations = response.data?.integrations?.attributes || [];
        const projectId = response.data?.projectId?.attributes;
        const token = response.data?.token?.attributes;
        this.$patch({
          integrations: integrations,
          projectId: projectId,
          token: token,
        });
      } catch (err: any) {
        this.$patch({
          error: err.message || 'Failed to fetch integrations',
        });
      } finally {
        this.$patch({
          loading: false,
        });
      }
    },
    async fetchStaticIntegrations() {
      this.staticIntegrationsLoading = true;
      try {
        const response = await Api.integrations.apps();
        this.staticIntegrationsData = response.data?.apps?.attributes || [];
      } catch {
        this.staticIntegrationsData = [];
      } finally {
        this.staticIntegrationsLoading = false;
      }
    },
    async sendTo(integration: string) {
      const itemStore = useViewerItemStore();
      if (!itemStore.id) {
        return;
      }
      await Api.integrations.send(integration, itemStore.id);
    },
    setIntegrationToken(token: string) {
      this.token = token;
    },
  },
  getters: {
    commingSoonIntegrations: state => state.commingSoonIntegrationsData,
    sortedComingSoonIntegrations: state => {
      return [...state.commingSoonIntegrationsData].sort((a, b) => a.name.localeCompare(b.name));
    },
    staticIntegrations: state => state.staticIntegrationsData,
    sortedStaticIntegrations: (state): StaticIntegration[] => {
      const canAccess = useEntitlementsStore().can('premiumIntegrations');

      return [...state.staticIntegrationsData]
        .map(integration => ({
          ...integration,
          disabled: integration.requiresPremiumAccess ? !canAccess : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    sortedIntegrations: (state): Integration[] => {
      const canAccess = useEntitlementsStore().can('premiumIntegrations');

      return [...state.integrations]
        .map(integration => ({
          ...integration,
          requiresRequest: !integration.enabled ? !canAccess : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
});
